import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import { Form, Formik } from "formik";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { DrawerForm } from "../../Common/styles/drawer";
import http from "../../../utils/http";
import { useParams } from "react-router-dom";

export const documentTypeOptions = [
  { label: "Protocol Documents", value: "Protocol Documents" },
  { label: "IRB document", value: "IRB document" },
  { label: "Agreements", value: "Agreements" },
];

const schema = yup.object().shape({
  documentName: yup.string().required("Document name is Required"),
  documentType: yup.string().required("Document type is Required"),
  documentCategory: yup.string().required("Document category is Required"),
});

type Props = {
  showModal: boolean;
  closeModal: () => void;
  documentId: string;
  documentName: string;
  documentType: string;
  documentCategory: string;
  onDocumentUpdated: () => void;
};

const EditDocumentModal = ({
  showModal,
  closeModal,
  documentId,
  documentName,
  documentType,
  documentCategory,
  onDocumentUpdated,
}: Props) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const { id: studyId, participantId } = useParams();

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);

      const body: any = {
        name: values?.documentName,
        type: values?.documentType,
        category: values?.documentCategory,
        participantId,
      };

      const res = await http.patch(
        `/participant-documents/study/${studyId}/${documentId}`,
        body
      );
      toastMessage(
        "success",
        res?.data?.message || "Document updated successfully"
      );
      closeModal();
      setSubmitLoader(false);
      onDocumentUpdated();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader title="Edit Document" onCloseClick={closeModal} />
        <Formik
          initialValues={{
            documentName: documentName,
            documentType: documentType,
            documentCategory: documentCategory,
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="document-name">
                  Document Name <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <TextField
                  placeholder="Enter document name"
                  id="document-name"
                  {...getFieldProps("documentName")}
                  error={
                    touched?.documentName && errors?.documentName ? true : false
                  }
                  helperText={
                    touched?.documentName && errors?.documentName
                      ? (errors?.documentName as string)
                      : " "
                  }
                />
              </FormControl>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="document-type">
                  Document Type <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Select
                  value={values?.documentType || ""}
                  onChange={(e) =>
                    setFieldValue("documentType", e.target.value)
                  }
                  fullWidth
                  id="document-type"
                >
                  {documentTypeOptions.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={
                    touched?.documentType && errors?.documentType ? true : false
                  }
                >
                  {touched?.documentType && errors?.documentType
                    ? (errors?.documentType as string)
                    : " "}
                </FormHelperText>
              </FormControl>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="document-category">
                  Document Category <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <TextField
                  placeholder="Enter document category"
                  id="document-category"
                  {...getFieldProps("documentCategory")}
                  error={
                    touched?.documentCategory && errors?.documentCategory
                      ? true
                      : false
                  }
                  helperText={
                    touched?.documentCategory && errors?.documentCategory
                      ? (errors?.documentCategory as string)
                      : " "
                  }
                />
              </FormControl>

              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitLoader ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        Update Document
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default EditDocumentModal;
